import { useCallback } from "react";

export const NETWORK_ERROR_MESSAGE =
  "No internet connection. Please check your network and try again.";

const STATUS_MESSAGES = {
  auth: {
    400: "Invalid request. Please check your credentials.",
    401: "Invalid username or password.",
    403: "Access denied. Please contact your administrator.",
    404: "Authentication service unavailable. Please try again later.",
    429: "Too many attempts. Please try again after 5 minutes.",
    500: "Internal server error. Please try again later.",
    default: "Login failed. Please try again.",
  },
  patient: {
    400: "Invalid patient data. Please check the entered fields.",
    401: "Session expired. Please log in again.",
    403: "You do not have permission to perform this action.",
    404: "Patient record not found.",
    409: "A patient with this contact information already exists.",
    422: "Validation failed. Please verify the submitted details.",
    500: "Server error. Please try again later.",
    default: "Operation failed due to an error",
  },
  form: {
    400: "Invalid request. Please check the entered data.",
    401: "Unauthorized. Please log in again.",
    403: "You do not have permission to perform this action.",
    404: "Failed to save data. Please try again.",
    409: "This record already exists.",
    422: "Validation error. Please verify the submitted data.",
    500: "Internal server error. Please try again later.",
    default: "An unexpected error occurred. Please try again.",
  },
};

export const getStatusErrorMessage = (status, context = "form") => {
  if (status >= 200 && status < 300) return "";
  const map = STATUS_MESSAGES[context] || STATUS_MESSAGES.form;
  return map[status] || map.default || "An error occurred. Please try again.";
};

/**
 * Extracts error message with FIRST PREFERENCE to backend response data.
 * Falls back to status-code message only if no backend message is provided.
 */
export const parseApiResponseError = async (
  response,
  context = "form",
  preParsedData = null
) => {
  if (!response || response.ok) {
    return "";
  }

  // 1. FIRST PREFERENCE: Message coming from backend
  try {
    const data =
      preParsedData ||
      (response.clone ? await response.clone().json() : await response.json());

    if (typeof data === "string" && data.trim() && !data.trim().startsWith("<")) {
      return data.trim();
    }

    const backendMsg =
      data?.message ||
      (typeof data?.error === "string" ? data.error : data?.error?.message) ||
      data?.resp ||
      data?.detail;

    if (
      backendMsg &&
      typeof backendMsg === "string" &&
      backendMsg.trim() !== "" &&
      backendMsg.trim().toLowerCase() !== "fail" &&
      backendMsg.trim().toLowerCase() !== "success"
    ) {
      return backendMsg.trim();
    }
  } catch {
    // Body is empty or not JSON
  }

  // 2. Fallback to status-code mapping
  return getStatusErrorMessage(response.status, context);
};

export const useApiError = (context = "form") => {
  const getErrorMessage = useCallback(
    (status) => getStatusErrorMessage(status, context),
    [context]
  );

  const parseErrorResponse = useCallback(
    (response, preParsedData = null) =>
      parseApiResponseError(response, context, preParsedData),
    [context]
  );

  return {
    getErrorMessage,
    parseErrorResponse,
    networkErrorMessage: NETWORK_ERROR_MESSAGE,
  };
};

export default useApiError;
